import { Children, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../assets/styles/SelectRecherche.css';

/**
 * Un <select> où l'on peut écrire : la saisie filtre la liste.
 *
 * Même contrat que le <select> natif, pour se substituer à lui sans toucher
 * aux formulaires : `value`, `onChange(e)` avec `e.target.value`, `disabled`,
 * `required`, `className` (posée sur le champ de saisie, pour que
 * `.param-select`, `.cal-select`… continuent de l'habiller) et des enfants
 * <option value>libellé</option>. Un <select> natif invisible reste dans le
 * DOM avec les mêmes options : c'est lui qui porte `required` et la
 * validation du formulaire.
 *
 * `multiple` : `value` est un tableau, chaque ligne de la liste se coche,
 * et `onChange` reçoit le tableau des valeurs cochées.
 *
 * La liste s'ouvre en portail sous <body>, en position fixe : dans une
 * modale ou une cellule (overflow caché ou défilant), elle serait rognée.
 */

/** Texte d'un enfant React, à plat : « {f.code} — {f.intitule} » donne un tableau. */
function texte(noeud) {
  if (noeud === null || noeud === undefined || typeof noeud === 'boolean') return '';
  if (typeof noeud === 'string' || typeof noeud === 'number') return String(noeud);
  if (Array.isArray(noeud)) return noeud.map(texte).join('');
  if (noeud.props) return texte(noeud.props.children);
  return '';
}

/** Les <option> enfants -> [{ value, label, disabled }]. */
function optionsDepuisEnfants(children) {
  const options = [];
  Children.forEach(children, (enfant) => {
    if (!enfant || !enfant.props) return;
    if (enfant.type === 'optgroup') {
      options.push(...optionsDepuisEnfants(enfant.props.children));
      return;
    }
    if (enfant.type !== 'option') return;
    const value = enfant.props.value ?? texte(enfant.props.children);
    options.push({
      value: String(value),
      label: (enfant.props.label ?? texte(enfant.props.children)).trim(),
      disabled: Boolean(enfant.props.disabled),
    });
  });
  return options;
}

const normaliser = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

function SelectRecherche({
  value,
  onChange,
  children,
  options: optionsProp,
  className = '',
  disabled = false,
  required = false,
  multiple = false,
  placeholder,
  name,
  id,
  'aria-label': ariaLabel,
  title,
}) {
  const options = useMemo(
    () => (optionsProp ? optionsProp.map((o) => ({ ...o, value: String(o.value) })) : optionsDepuisEnfants(children)),
    [optionsProp, children]
  );

  const valeurs = useMemo(() => {
    if (multiple) return (Array.isArray(value) ? value : value ? [value] : []).map(String);
    return value === null || value === undefined ? [''] : [String(value)];
  }, [value, multiple]);

  const choisies = options.filter((o) => valeurs.includes(o.value));
  const libelleCourant = multiple
    ? choisies.length === 0
      ? ''
      : choisies.length === 1
        ? choisies[0].label
        : `${choisies.length} sélectionnées`
    : (choisies[0] && choisies[0].label) || '';

  const [ouverte, setOuverte] = useState(false);
  const [saisie, setSaisie] = useState('');
  const [surligne, setSurligne] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const champ = useRef(null);
  const liste = useRef(null);
  const idListe = useId();

  const filtrees = useMemo(() => {
    const q = normaliser(saisie.trim());
    if (!q) return options;
    return options.filter((o) => normaliser(o.label).includes(q));
  }, [options, saisie]);

  function mesurer() {
    if (!champ.current) return;
    const r = champ.current.getBoundingClientRect();
    setPosition({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
  }

  function ouvrir() {
    if (disabled) return;
    mesurer();
    setSaisie('');
    const idx = filtrees.findIndex((o) => valeurs.includes(o.value));
    setSurligne(idx >= 0 ? idx : 0);
    setOuverte(true);
  }

  function fermer() {
    setOuverte(false);
    setSaisie('');
  }

  function emettre(nouvelles) {
    if (!onChange) return;
    onChange({
      target: {
        value: multiple ? nouvelles : nouvelles[0] ?? '',
        name,
        type: multiple ? 'select-multiple' : 'select-one',
      },
    });
  }

  function choisir(option) {
    if (!option || option.disabled) return;
    if (multiple) {
      const suivantes = valeurs.includes(option.value)
        ? valeurs.filter((v) => v !== option.value)
        : [...valeurs, option.value];
      emettre(suivantes);
      // La liste reste ouverte : on coche plusieurs lignes d'affilée.
      return;
    }
    emettre([option.value]);
    fermer();
  }

  // Fermer quand on clique ailleurs, et quand la page défile ou change de
  // taille : la liste est posée en coordonnées fixes, elle ne suivrait pas.
  useEffect(() => {
    if (!ouverte) return undefined;
    const clicDehors = (e) => {
      if (champ.current?.parentElement?.contains(e.target)) return;
      if (liste.current?.contains(e.target)) return;
      fermer();
    };
    const defile = (e) => {
      if (liste.current?.contains(e.target)) return;
      fermer();
    };
    document.addEventListener('mousedown', clicDehors);
    window.addEventListener('scroll', defile, true);
    window.addEventListener('resize', fermer);
    return () => {
      document.removeEventListener('mousedown', clicDehors);
      window.removeEventListener('scroll', defile, true);
      window.removeEventListener('resize', fermer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte]);

  // La ligne surlignée reste visible quand on descend au clavier.
  useEffect(() => {
    if (!ouverte || !liste.current) return;
    const el = liste.current.querySelector(`[data-index="${surligne}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [surligne, ouverte]);

  useEffect(() => {
    setSurligne(0);
  }, [saisie]);

  function clavier(e) {
    if (!ouverte) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        ouvrir();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSurligne((i) => Math.min(filtrees.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSurligne((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choisir(filtrees[surligne]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      fermer();
    } else if (e.key === 'Tab') {
      fermer();
    }
  }

  const classeSaisie = ['selrec-saisie', className].filter(Boolean).join(' ');

  return (
    <div className={`selrec${multiple ? ' selrec-multiple' : ''}${disabled ? ' selrec-desactive' : ''}`}>
      <input
        ref={champ}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={ouverte}
        aria-controls={idListe}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        title={title || (ouverte ? undefined : libelleCourant || undefined)}
        className={classeSaisie}
        value={ouverte ? saisie : libelleCourant}
        placeholder={ouverte ? libelleCourant || placeholder || 'Rechercher…' : placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={ouvrir}
        onClick={() => !ouverte && ouvrir()}
        onChange={(e) => {
          if (!ouverte) ouvrir();
          setSaisie(e.target.value);
        }}
        onKeyDown={clavier}
      />
      <span className="selrec-chevron" aria-hidden="true">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 4.5l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>

      {/* Le <select> natif, hors de vue : `required` et la bulle de
          validation du navigateur, sans réinventer la règle. */}
      <select
        className="selrec-natif"
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        multiple={multiple}
        required={required}
        disabled={disabled}
        value={multiple ? valeurs : valeurs[0]}
        onChange={() => {}}
      >
        {!multiple && !options.some((o) => o.value === '') && <option value="" />}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {multiple && choisies.length > 0 && (
        <div className="selrec-chips">
          {choisies.map((o) => (
            <span className="selrec-chip" key={o.value}>
              {o.label}
              {!disabled && (
                <button
                  type="button"
                  className="selrec-chip-retirer"
                  aria-label={`Retirer ${o.label}`}
                  onClick={() => emettre(valeurs.filter((v) => v !== o.value))}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {ouverte &&
        createPortal(
          <ul
            ref={liste}
            id={idListe}
            role="listbox"
            aria-multiselectable={multiple || undefined}
            className="selrec-liste"
            style={{ top: position.top, left: position.left, width: position.width }}
          >
            {filtrees.length === 0 && <li className="selrec-vide">Aucun résultat</li>}
            {filtrees.map((o, i) => {
              const coche = valeurs.includes(o.value);
              return (
                <li
                  key={o.value || `vide-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={coche}
                  aria-disabled={o.disabled || undefined}
                  className={
                    'selrec-option' +
                    (i === surligne ? ' selrec-option-surligne' : '') +
                    (coche ? ' selrec-option-choisie' : '') +
                    (o.disabled ? ' selrec-option-desactivee' : '')
                  }
                  onMouseEnter={() => setSurligne(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choisir(o)}
                >
                  {multiple && (
                    <span className={`selrec-case${coche ? ' selrec-case-cochee' : ''}`} aria-hidden="true">
                      {coche ? '✓' : ''}
                    </span>
                  )}
                  <span className="selrec-option-libelle">{o.label || ' '}</span>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
}

export default SelectRecherche;
